/**
 * 数字工具类
 * 处理数字比较和数学运算
 */

/**
 * 在两个数字之间进行大小比较，提供最小值
 * @param a 第一个数字
 * @param b 第二个数字
 * @returns 两个数字中的最小值
 */
export function getMinValue(a: number, b: number): number {
  return Math.min(a, b);
}

/**
 * 在两个数字之间进行大小比较，提供最大值
 * @param a 第一个数字
 * @param b 第二个数字
 * @returns 两个数字中的最大值
 */
export function getMaxValue(a: number, b: number): number {
  return Math.max(a, b);
}

/**
 * 比较两个数字的大小关系
 * @param a 第一个数字
 * @param b 第二个数字
 * @returns 比较结果：-1表示a<b，0表示a=b，1表示a>b
 */
export function compareNumbers(a: number, b: number): -1 | 0 | 1 {
  if (a < b) {
    return -1;
  }
  if (a > b) {
    return 1;
  }
  return 0;
}

/**
 * 检查两个数字是否相等（考虑浮点数精度）
 * @param a 第一个数字
 * @param b 第二个数字
 * @param precision 精度，默认为0.000001
 * @returns 是否相等
 */
export function isEqual(
  a: number,
  b: number,
  precision: number = 0.000001,
): boolean {
  return Math.abs(a - b) < precision;
}

/**
 * 获取多个数字中的最小值
 * @param numbers 数字数组
 * @returns 最小值，如果数组为空则返回Infinity
 */
export function getMinValueFromArray(numbers: number[]): number {
  if (numbers.length === 0) {
    return Infinity;
  }
  return Math.min(...numbers);
}

/**
 * 获取多个数字中的最大值
 * @param numbers 数字数组
 * @returns 最大值，如果数组为空则返回-Infinity
 */
export function getMaxValueFromArray(numbers: number[]): number {
  if (numbers.length === 0) {
    return -Infinity;
  }
  return Math.max(...numbers);
}

/**
 * 限制数字在指定范围内
 * @param value 要限制的数字
 * @param min 最小值
 * @param max 最大值
 * @returns 限制后的数字
 */
export function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max);
}

/**
 * 检查数字是否在指定范围内
 * @param value 要检查的数字
 * @param min 最小值
 * @param max 最大值
 * @param inclusive 是否包含边界值，默认为true
 * @returns 是否在范围内
 */
export function isInRange(
  value: number,
  min: number,
  max: number,
  inclusive: boolean = true,
): boolean {
  if (inclusive) {
    return value >= min && value <= max;
  }
  return value > min && value < max;
}

/**
 * 将带 value 的项归一化到目标和：前 n-1 项按 precision 四舍五入，最后一项 = targetSum - 前面之和（保证总和且避免条带/图表溢出）
 * @param items 含 value 的数组（不会被修改）
 * @param targetSum 目标和，默认 100（百分比）
 * @param precision 小数位数，默认 2
 * @returns 新数组，每项为浅拷贝并带有归一化后的 value
 */
export function normalizeValuesToSum<T extends { value: number }>(
  items: T[],
  targetSum: number = 100,
  precision: number = 2,
): T[] {
  if (items.length === 0) {
    return [];
  }
  const round = (v: number) =>
    Math.round(v * Math.pow(10, precision)) / Math.pow(10, precision);
  if (items.length === 1) {
    return [{ ...items[0], value: round(targetSum) }];
  }
  const rest = items.slice(0, -1).map((item) => ({
    ...item,
    value: round(item.value),
  }));
  const sumRest = rest.reduce((s, i) => s + i.value, 0);
  const last = items[items.length - 1];
  const lastValue = Math.max(0, round(targetSum - sumRest));
  return [...rest, { ...last, value: lastValue }];
}

/** 将数字格式化为html */
/**
 * 构建带有角标0计数的数字格式的HTML字符串
 * @param integerPart 整数部分
 * @param zeroCount 小数点后连续0的个数
 * @param restOfDecimal 0之后剩余的小数部分
 * @param spanClass 用于角标的CSS类名
 * @returns 格式化后的HTML字符串
 */
export function buildSubscriptNumberHtml({
  integerPart,
  zeroCount,
  restOfDecimal,
  spanClass,
}: {
  integerPart: string;
  zeroCount: number;
  restOfDecimal: MaybeNullable<string>;
  spanClass?: string;
}): string {
  if (zeroCount > 0) {
    return `${integerPart}.0<span class="${spanClass || ""}">${zeroCount}</span>${restOfDecimal}`;
  }
  if (restOfDecimal && restOfDecimal.length > 0) {
    return `${integerPart}.${restOfDecimal}`;
  }
  return `${integerPart}`;
}

/** 将数字格式化为html */
export function formatNumberToHtml(input: number | string, spanClass?: string) {
  let inputStr = "";

  // 如果是number类型，转为字符串
  if (typeof input === "number") {
    inputStr = String(input);
  } else if (typeof input === "string") {
    inputStr = input;
  } else {
    return;
  }

  // 正则表达式，用于判断字符串是否为合法数字
  // ^ - 字符串开头
  // \d+ - 一位或多位数字
  // (\.\d+)? - 一个可选的小数点，如果存在，后面必须跟至少一位数字
  // $ - 字符串结尾
  const isValidNumber =
    /^\d+(\.\d+)?$/.test(inputStr) && !/[eE]/.test(inputStr);

  if (!isValidNumber) {
    return;
  }

  // 以小数点分割
  const parts = inputStr.split(".");

  // 如果没有小数点，或者小数点后紧跟的0不超过3位
  if (parts.length === 1 || !parts[1].startsWith("0")) {
    return inputStr;
  }

  const integerPart = parts[0];
  const decimalPart = parts[1];

  let zeroCount = 0;
  // 计算小数点后紧跟的0的个数
  for (let i = 0; i < decimalPart.length; i++) {
    if (decimalPart[i] === "0") {
      zeroCount++;
    } else {
      break;
    }
  }

  if (zeroCount <= 3) {
    return inputStr;
  } else {
    // 0后面的其他数字
    const restOfDecimal = decimalPart.substring(zeroCount);
    return buildSubscriptNumberHtml({
      integerPart,
      zeroCount,
      restOfDecimal,
      spanClass,
    });
  }
}
